// ─────────────────────────────────────────
// i18n/loader.ts — Dynamic translation loader
// Monta a Translation a partir dos arquivos de seção
// em locales/<locale>/<pagina>/<secao>.json.
// Tauvior — IA Edge-first
// ─────────────────────────────────────────

import type { Locale, Translation } from './types';
import { detectLocale } from './detector';

/** Cached active translation — set after first load */
let activeTranslation: Translation | null = null;
let activeLocale: Locale | null = null;

/**
 * Um arquivo por seção, agrupado em uma pasta por página:
 * `locales/<locale>/<pagina>/<secao>.json`. A pasta organiza o conteúdo —
 * quem serve o quê — mas não entra na chave: `grid/grid.hero.json` continua
 * sendo `t.grid.hero`. Por isso o nome da seção precisa ser único no locale
 * inteiro, e não só dentro da pasta (ver a checagem em importTranslation).
 *
 * O glob é resolvido em build time pelo Vite; cada arquivo vira um import
 * dinâmico, e o vite.config agrupa todos os de um mesmo locale num único
 * chunk (locale-pt-BR / locale-en-US).
 */
const sectionModules = import.meta.glob<{ default: unknown }>('./locales/*/*/*.json');

/**
 * `locale.json` carrega lang/locale/dir — vai espalhado na raiz da
 * Translation. Todos os outros arquivos viram uma chave com o nome
 * do arquivo (hero.json → t.hero). Pontos no nome aninham: uma seção
 * grande pode ser dividida em um arquivo por bloco (home.hero.json →
 * t.home.hero, home.faq.json → t.home.faq).
 */
const ROOT_SECTION = 'locale';

/**
 * Seções que o renderer espera encontrar. Um arquivo esquecido em um
 * locale falharia lá na frente com "cannot read property of undefined";
 * aqui o erro aponta o arquivo que falta.
 */
const REQUIRED_SECTIONS: readonly string[] = [
  'meta',
  'nav',
  'home',
  'advisory',
  'empresa',
  'edge',
  'grid',
  'sobre',
  'cta',
  'contact',
  'contato',
  'footer',
];

async function importTranslation(locale: Locale): Promise<Translation> {
  const prefix = `./locales/${locale}/`;
  const entries = Object.entries(sectionModules).filter(([path]) =>
    path.startsWith(prefix),
  );

  if (entries.length === 0) {
    throw new Error(`[i18n] No section files found for locale "${locale}".`);
  }

  const sections = await Promise.all(
    entries.map(async ([path, load]) => {
      // './locales/pt-BR/grid/grid.json' → 'grid/grid' → 'grid'
      const relative = path.slice(prefix.length, -'.json'.length);
      const name = relative.slice(relative.lastIndexOf('/') + 1);
      const { default: content } = await load();
      return [name, relative, content] as const;
    }),
  );

  const draft: Record<string, unknown> = {};
  const origin = new Map<string, string>();

  for (const [name, relative, content] of sections) {
    // A pasta não faz parte da chave: dois arquivos com o mesmo nome em
    // pastas diferentes se sobrescreveriam em silêncio, e a página que
    // perdesse a disputa renderizaria o texto da outra.
    const previous = origin.get(name);
    if (previous) {
      throw new Error(
        `[i18n] Seção duplicada "${name}" em "${locale}": ` +
        `${previous}.json e ${relative}.json. Nomes de seção são únicos no locale.`,
      );
    }
    origin.set(name, relative);

    if (name === ROOT_SECTION) {
      Object.assign(draft, content as Record<string, unknown>);
      continue;
    }

    // 'home.hero' → draft.home.hero. Um arquivo inteiro (home.json) e
    // arquivos por bloco (home.hero.json) da mesma seção não convivem:
    // um sobrescreveria o outro dependendo da ordem de carga.
    const keys = name.split('.');
    const last = keys.pop()!;
    let parent = draft;
    for (const [i, key] of keys.entries()) {
      const prefix = keys.slice(0, i + 1).join('.');
      if (origin.has(prefix)) {
        throw new Error(
          `[i18n] "${relative}.json" conflita com "${origin.get(prefix)}.json" em "${locale}".`,
        );
      }
      parent = (parent[key] ??= {}) as Record<string, unknown>;
    }
    if (parent[last] !== undefined) {
      throw new Error(`[i18n] "${relative}.json" conflita com outro arquivo em "${locale}".`);
    }
    parent[last] = content;
  }

  const missing = REQUIRED_SECTIONS.filter((name) => draft[name] === undefined);
  if (missing.length > 0) {
    throw new Error(
      `[i18n] Missing section files for "${locale}": ${missing
        .map((name) => `${name}.json`)
        .join(', ')}`,
    );
  }

  return draft as unknown as Translation;
}

/**
 * Loads and caches the translation for the detected locale.
 * Safe to call multiple times — only fetches once.
 */
export async function loadTranslation(): Promise<Translation> {
  if (activeTranslation) return activeTranslation;

  const locale = detectLocale();
  activeTranslation = await importTranslation(locale);
  activeLocale = locale;

  return activeTranslation;
}

/**
 * Returns the active locale after loadTranslation() has been called.
 * Throws if called before loading.
 */
export function getLocale(): Locale {
  if (!activeLocale) throw new Error('[i18n] Translation not loaded yet. Call loadTranslation() first.');
  return activeLocale;
}

/**
 * Returns the cached translation synchronously.
 * Throws if called before loadTranslation() resolves.
 */
export function getTranslation(): Translation {
  if (!activeTranslation) throw new Error('[i18n] Translation not loaded yet. Call loadTranslation() first.');
  return activeTranslation;
}
